import { Action, Dispatch } from 'redux';
import { History } from 'history';
import { ThunkDispatch } from 'redux-thunk';
import { AxiosError } from 'axios';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { showSuccessMessage, showError } from 'shared/toaster';
import { SYSTEM_CONFIGURATION_ROUTE, NETWORK, XDP } from '../../constants';
import { extractErrorMessage } from '../../utils';
import * as api from './xdp.api';
import {
  Xdp,
  GetXdpAction,
  GetSingleXdpAction,
  SelectXdp,
  DeleteXdpThunkAction,
  CreateXdpThunkAction,
  UpdateXdpThunkAction,
  GET_XDPS,
  GET_XDP,
  GO_TO_XDP_CREATE,
  GO_TO_XDP_EDIT,
  GO_TO_XDP_VIEW,
  GO_TO_XDP_LIST,
  CREATE_XDP,
  DELETE_XDP,
  UPDATE_XDP,
  SELECT_XDP,
  XdpServerResponse,
} from './xdp.types';
import { getSelectedXdp } from './xdp.selectors';

export function getXdps(deliveryFunctionId: string): GetXdpAction {
  return {
    type: GET_XDPS,
    payload: api.getXdps(deliveryFunctionId),
  };
}

export function getXdp(deliveryFunctionId: string, xdpId: string): GetSingleXdpAction {
  return {
    type: GET_XDP,
    payload: api.getXdp(deliveryFunctionId, xdpId),
  };
}

export function createXdp(
  deliveryFunctionId: string,
  body: WithAuditDetails<XdpServerResponse>,
  history: History,
): CreateXdpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_XDP,
        payload: api.createXdp(deliveryFunctionId, body),
      });

      dispatch(goToXdpList(history));
      showSuccessMessage(i18n.t('xdp.create.success', { name: body.data.IPDUID }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('xdp.create.failed', { name: body.data.IPDUID, error: errorMessage }));
    }
  };
}

export function updateXdp(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<XdpServerResponse>,
  history: History,
): UpdateXdpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_XDP,
        payload: api.updateXdp(deliveryFunctionId, id, body),
      });

      dispatch(goToXdpList(history));
      showSuccessMessage(i18n.t('xdp.edit.success', { name: body.data.IPDUID }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('xdp.edit.failed', { name: body.data.IPDUID, error: errorMessage }));
    }
  };
}

export function deleteXdp(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteXdpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedXdp] = getSelectedXdp(getState());

    try {
      await dispatch({
        type: DELETE_XDP,
        payload: api.deleteXdp(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('xdp.delete.success', { name: selectedXdp?.ipduId }), 5000);
      if (history != null) {
        dispatch(goToXdpList(history));
      }
    } catch (e) {
      showError(i18n.t('xdp.delete.failed', { name: selectedXdp?.ipduId }));

    } finally {
      dispatch(getXdps(deliveryFunctionId));
    }
  };
}

export function selectXdp(
  xdp: Xdp[],
): SelectXdp {
  return {
    type: SELECT_XDP,
    payload: xdp,
  };
}

export function goToXdpList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XDP_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XDP}`);
  };
}

export function goToXdpCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XDP_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XDP}/create`);
  };
}

export function goToXdpEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XDP_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XDP}/edit`);
  };
}

export function goToXdpView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XDP_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XDP}/view`);
  };
}
